import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  DatePicker,
  message,
  Tabs
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  CheckOutlined
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import { apiResolvedWarning, apiGetAllWarnings } from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import { convertLocaltimeToUTC, downloadBlobFile, warningTypes } from '@utils/utils';
import { useNavigate } from 'react-router-dom';
import { UploadFileOutlined } from '@mui/icons-material';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { ADMIN_ROLE } from '@utils/constant';
import TabPane from 'antd/es/tabs/TabPane';
import dayjs from 'dayjs';

const { Option } = Select;

const { RangePicker } = DatePicker;

export default function List() {
  const [type, setType] = useState('ALL');
  const [warningList, setWarningList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [status, setStatus] = useState('0');
  const [dateRange, setDateRange] = useState([null, null]);
  const [rangeDateString, setRangeDateString] = useState([null, null]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await apiGetAllWarnings({
        page: currentPage,
        size: pageSize,
        fromDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
          : undefined,
        toDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
          : undefined,
        type: type === 'ALL' ? undefined : type,
        status: status === 'all' ? undefined : Number(status),
        keyWord: search ?? undefined
      });
      setWarningList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolved = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn cập nhật?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiResolvedWarning(record?.id);
        message.success('Cập nhật thành công!');
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, status]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onChangeDateRange = (dates, dateStrings) => {
    setRangeDateString(dateStrings);
    setDateRange(dates);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 50,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        fixed: 'left',
        render: (text, record) => {
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {record?.status === 1 ? (
                  <CheckCircleFilled style={{ fontSize: '20px', color: '#52c41a' }} />
                ) : (
                  <CloseCircleFilled style={{ fontSize: '20px', color: '#ff4d4f' }} />
                )}
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    color: record?.status === '1' ? '#52c41a' : '#ff4d4f'
                  }}>
                  {record?.status === 1 ? 'Đã xử lý' : 'Chưa xử lý'}
                </Typography.Text>
              </div>
            </>
          );
        },
        width: 120
      },
      {
        title: 'Số Sêri',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 100
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Tên KH',
        dataIndex: 'customer_name',
        fixed: 'left',
        width: 150
      },
      {
        title: 'Cảnh báo',
        dataIndex: 'name',
        fixed: 'left',
        width: 150
      },
      {
        title: 'Thời gian',
        dataIndex: 'created_at',
        render: (text, record) => {
          return <>{moment(record?.created_at)?.format('DD/MM/YYYY HH:mm')}</>;
        },
        width: 100
      },
      {
        title: 'Người xử lý',
        dataIndex: 'resolved_by',
        width: 100
      },
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            {user?.user?.role === ADMIN_ROLE && record?.status == 0 && (
              <>
                <Button
                  onClick={() => handleResolved(record)}
                  icon={<CheckOutlined />}
                  style={{
                    backgroundColor: '#F4F5F7',
                    color: '#0174CF',
                    border: 'none',
                    marginRight: '5px'
                  }}>
                  Đánh dấu là đã xử lý
                </Button>
              </>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const generateMonthOptions = () => {
    const months = [];
    const current = moment();
    const startOfYear = current.clone().startOf('year');

    for (let m = 0; m <= current.month() + 1; m++) {
      const monthMoment = startOfYear.clone().add(m, 'months');
      months.push({
        label: `Tháng cước ${monthMoment.format('M')}`,
        value: monthMoment.format('YYYY-MM-DD')
      });
    }
    return months;
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/configs/export-report`,
        `Danh sách cấu hình chốt số kỳ ${moment(selectedMonth).format('MM-YYYY')}.xlsx`,
        {
          keyWord: search ?? undefined,
          period: selectedMonth ?? undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    if (user?.user?.role !== ADMIN_ROLE) {
      showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
      return;
    }
    navigate('/config/add');
  };

  return (
    <div className="dashboard">
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0174CF' }}>
            Thống kê cảnh báo
          </Typography.Title>
        </Col>
        <Col>
          {/* <Space>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddDevice}
              style={{
                color: '#0174CF',
                height: '36px',
                backgroundColor: '#E8EBEF',
                borderColor: '#E8EBEF',
                fontSize: '18px'
              }}>
              Thêm mới
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={handleExportExcelButtonClick}
              loading={loading}
              style={{
                color: '#fff',
                height: '36px',
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                fontSize: '18px'
              }}>
              Xuất file excel
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                if (user?.user?.role !== ADMIN_ROLE) {
                  showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
                  return;
                }
                setVisibleModalImport(true);
              }}
              style={{
                color: '#fff',
                height: '36px',
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                fontSize: '18px'
              }}>
              Nhập file excel
            </Button>
          </Space> */}
        </Col>
      </Row>
      <Card>
        <Row gutter={[8, 8]}>
          <Col md={6} xs={12}>
            <Select value={type} onChange={setType} style={{ width: '100%', height: '36px' }}>
              {warningTypes.map((warning) => (
                <Option key={warning.value} value={warning.value}>
                  {warning.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={6} xs={12}>
            <RangePicker
              value={dateRange}
              onChange={onChangeDateRange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Col>
          <Col md={6} xs={12}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', marginBottom: '16px', height: '36px' }}
            />
          </Col>
          <Col md={6} sm={12}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
        <Tabs activeKey={status} defaultActiveKey={'0'} onChange={(value) => setStatus(value)}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              bordered
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: '4px',
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Chưa xử lý`} key={'0'}>
            <Table
              bordered
              // size="middle"
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: '4px',
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Đã xử lý`} key={'1'}>
            <Table
              bordered
              // size="middle"
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: '4px',
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
